import { useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import { FormGroup, Label } from "reactstrap";
import CustomSelect from "../../../components/CustomSelect";
import TemplateInputs from "./templateInputs";
import { fetchExportCustomFields } from "../../adminExports/data/customFieldsService";
import { flattenTemplateFields, resolveEffectiveTemplateState } from "../../adminExports/data/customFieldsTree";
import { getRegionDefaultValue } from "../../../utils/regionDefaultValue";

const coerceToString = (v) => (v === null || v === undefined ? "" : String(v));
const toBool = (v) => v === true || v === "true" || v === 1 || v === "1";
const TYPE_DATE = "Date";
const normalizeDateValue = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const ymdPrefixMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymdPrefixMatch) return ymdPrefixMatch[1];

  const parsed = Date.parse(raw);
  if (Number.isNaN(parsed)) return "";

  return new Date(parsed).toISOString().slice(0, 10);
};
const mergeQuoteAdditionalFields = (existingFields, updatedFields) => {
  const getKey = (item) => {
    const field = String(item?.field ?? "").trim();
    if (field) return `field:${field}`;

    const id = String(item?.id ?? "").trim();
    if (id) return `id:${id}`;

    return null;
  };

  const merged = new Map();

  (Array.isArray(existingFields) ? existingFields : []).forEach((item) => {
    const key = getKey(item);
    if (!key || merged.has(key)) return;
    merged.set(key, item);
  });

  // Updated values replace matching existing keys while keeping unrelated fields.
  (Array.isArray(updatedFields) ? updatedFields : []).forEach((item) => {
    const key = getKey(item);
    if (!key) return;
    merged.set(key, item);
  });

  return Array.from(merged.values());
};

const ExportContent = ({
  exportTemplateData = [],
  selectedTemplate,
  setSelectedTemplate,
  quoteRevision,
  customContent,
  onUpdateQuoteAdditionalFields = () => {},
}) => {
  const [sectionValues, setSectionValues] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);
  const [globalFields, setGlobalFields] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadGlobalFields = async () => {
      const data = await fetchExportCustomFields();
      if (!isMounted) return;
      setGlobalFields(Array.isArray(data) ? data : []);
    };

    loadGlobalFields();

    return () => {
      isMounted = false;
    };
  }, []);


  const globalById = useMemo(() => {
    const map = new Map();
    (Array.isArray(globalFields) ? globalFields : []).forEach((field) => {
      if (field?.id === undefined || field?.id === null) return;
      map.set(String(field.id), field);
    });
    return map;
  }, [globalFields]);
  const sectionNameByGuid = useMemo(() => {
    const map = new Map();
    resolveEffectiveTemplateState(selectedTemplate?.customFields, selectedTemplate?.customSections, selectedTemplate?.customFieldsLayout).sections.forEach((section) => {
      if (!section?.guid) return;
      map.set(section.guid, String(section?.name || "").trim());
    });
    return map;
  }, [selectedTemplate]);

  const customFields = useMemo(() => {
    const links = flattenTemplateFields(selectedTemplate?.customFields, selectedTemplate?.customSections, selectedTemplate?.customFieldsLayout);
    return links
      .map((link) => {
        const globalField = globalById.get(String(link?.id));
        if (!globalField) return null;
        const sectionName = sectionNameByGuid.get(String(link?.sectionGuid || "")) || "";

        return {
          ...globalField,
          id: globalField.id,
          sectionGuid: String(link?.sectionGuid || ""),
          sectionName,
          section: sectionName,
          sortOrder: link?.sortOrder ?? 0,
          type: globalField?.type === "TextArea(WYSIWYG)" ? "TextArea" : globalField?.type,
        };
      })
      .filter(Boolean);
  }, [selectedTemplate, globalById, sectionNameByGuid]);

  const groupedTemplateOptions = useMemo(() => {
    const sheets = (Array.isArray(exportTemplateData) ? exportTemplateData : []).filter(x => x.type === "sheet");

    const sheetOptions = (Array.isArray(sheets) ? sheets : []).map((template) => ({
      value: `sheet-${template.id}`,
      label: template.name,
      template,
    }));

    const documentTemplates = (Array.isArray(exportTemplateData) ? exportTemplateData : []).filter(x => x.type === "document");

    const documentOptions = (Array.isArray(documentTemplates) ? documentTemplates : [])
        .map((template) => ({
      value: `document-${template.id}`,
      label: template.name,
      template: { ...template, type: "document" },
    }));

    return [
      {
        label: "Documents",
        options: documentOptions,
      },
      {
        label: "Sheets",
        options: sheetOptions,
      },
    ];
  }, [exportTemplateData]);

  const selectedValue = selectedTemplate
    ? `${selectedTemplate.type || "sheet"}-${selectedTemplate.id}`
    : null;

  useEffect(() => {
    if (!selectedTemplate) {
      setSectionValues({});
      return;
    }

    const initial = {};
    const existing = Array.isArray(quoteRevision?.quoteAdditionalFields)
      ? quoteRevision.quoteAdditionalFields
      : [];

    customFields.forEach((s) => {
      const found = existing.find((x) => x.field === s.field);
      const resolved = (Array.isArray(customContent) ? customContent : []).find((item) => item.field === s.field);
      const value = resolved ? resolved.value : found ? found.value : s.defaultValue ?? "";

      if (s.type === "Region") {
        initial[s.field] = found
          ? toBool(found.value)
          : getRegionDefaultValue(resolved ? resolved.value : s.defaultValue);
      } else if (s.type === TYPE_DATE) {
        initial[s.field] = normalizeDateValue(value);
      } else {
        initial[s.field] = coerceToString(value);
      }
    });

    setSectionValues(initial);
  }, [selectedTemplate, quoteRevision?.quoteAdditionalFields, customFields, customContent]);

  const buildPayload = useCallback(
    (values) => {
      const updatedFields = customFields.map((s) => ({
        id: s.id,
        field: s.field,
        value:
          s.type === "Region"
            ? String(!!values?.[s.field])
            : s.type === TYPE_DATE
            ? normalizeDateValue(values?.[s.field])
            : coerceToString(values?.[s.field]),
      }));

      return mergeQuoteAdditionalFields(quoteRevision?.quoteAdditionalFields, updatedFields);
    },
    [customFields, quoteRevision?.quoteAdditionalFields]
  );

  const debouncedSave = useMemo(
    () =>
      debounce((values) => {
        const payload = buildPayload(values);
        onUpdateQuoteAdditionalFields(payload);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(null), 1200);
      }, 500),
    [buildPayload, onUpdateQuoteAdditionalFields]
  );

  useEffect(() => {
    return () => debouncedSave.cancel();
  }, [debouncedSave]);

  const onInputChanged = (fieldName, fieldValue) => {
    setSaveStatus("saving");
    setSectionValues((prev) => {
      const next = { ...prev, [fieldName]: fieldValue };
      debouncedSave(next);
      return next;
    });
  };

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: 0, overflow: "hidden" }}>
      <FormGroup className="flex-shrink-0">
        <Label className="fw-semibold">Export Template</Label>
        <CustomSelect
          value={selectedValue}
          options={groupedTemplateOptions}
          onChange={(opt) => setSelectedTemplate(opt?.template ?? null)}
          allowWhenLocked
          placeholder="< Please select >" 
          data-testid="export-template-select"
        />
      </FormGroup>

      {selectedTemplate && customFields.length > 0 && (
        <div
          className="d-flex flex-column"
          style={{ flex: "1 1 0", minHeight: 0, height: 0, overflow: "hidden" }}
        >
          <TemplateInputs
            selectedTemplate={selectedTemplate}
            sectionValues={sectionValues}
            onInputChanged={onInputChanged}
            saveStatus={saveStatus}
          />
        </div>
      )}
    </div>
  );
};

export default ExportContent;
